<script lang="ts" setup>
  type Props = {
    images?: string[]
    matchDesktop?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    images: () => [],
    matchDesktop: false,
  })
  const slides = useTemplateRef('slides')
  const slideWidth = ref(0)
  const slideIndex = ref(0)
  const sensitivity = ref(50)

  const isMouseDown = ref(false)
  const delta = ref(0)
  const lastTouchPositionX = ref(0)

  const shouldSwipe = computed(() => Math.abs(delta.value) > sensitivity.value && Math.abs(delta.value) < sensitivity.value * 2)

  function handleMouseMove(e: TouchEvent) {
    if (e.touches.length > 1 || !isMouseDown.value) {
      return
    }
    const touchX = e.touches[0].screenX
    delta.value = touchX - lastTouchPositionX.value
    console.log(shouldSwipe.value)
    if (shouldSwipe.value) {
      onSlide(-1 * Math.sign(delta.value))
      delta.value = 0
      lastTouchPositionX.value = touchX
      isMouseDown.value = false
    }
  }

  function handleMouseDown(e: TouchEvent) {
    if (props.matchDesktop) {
      return
    }
    isMouseDown.value = true
    lastTouchPositionX.value = e.touches[0].screenX
  }

  function handleMouseUp() {
    isMouseDown.value = false
    delta.value = 0
    lastTouchPositionX.value = 0
  }

  function checkSlideWidth(): void {
    if (slideWidth.value === 0) {
      slideWidth.value = slides.value?.clientWidth ?? 0
    }
  }

  function onSlide(direction: number) {
    if (slideIndex.value + direction >= props.images.length || slideIndex.value + direction < 0)
      return
    checkSlideWidth()
    slideIndex.value += direction
  }

  function onSlideTo(index: number) {
    checkSlideWidth()
    slideIndex.value = index
  }

  const slideStyle = computed(() => `transform: translateX(-${slideIndex.value * slideWidth.value - delta.value}px);
    ${
    (isMouseDown.value && !shouldSwipe.value) || isMouseDown.value ? '' : 'transition: all 1s ease;'
  }`)

  onMounted(() => {
    slides.value?.addEventListener('touchstart', handleMouseDown)
    slides.value?.addEventListener('touchmove', handleMouseMove)
    slides.value?.addEventListener('touchend', handleMouseUp)
  })
</script>

<template>
  <div class="c-carousel">
    <div
      ref="slides"
      :style="slideStyle"
      class="slides"
    >
      <img
        v-for="(url, index) in props.images"
        :key="`image-${index}`"
        class="slide"
        :src="url"
        alt="bookandgolf-img"
        draggable="false"
      >
    </div>
    <div
      v-show="props.matchDesktop"
      class="button-left"
      :class="{ disabled: slideIndex === 0 }"
      @click="onSlide(-1)"
    >
      <SvgIcon
        :height="21"
        :width="21"
        icon="chevron"
      />
    </div>
    <div
      v-show="props.matchDesktop"
      class="button-right"
      :class="{ disabled: slideIndex === props.images.length - 1 }"
      @click="onSlide(1)"
    >
      <SvgIcon
        :height="21"
        :width="21"
        icon="chevron"
      />
    </div>
    <div class="dots">
      <div
        v-for="index in props.images.length"
        :key="`dot-${index}`"
        class="dot"
        :class="{ active: slideIndex === index - 1 }"
        @click="onSlideTo(index - 1)"
      />
    </div>
  </div>
</template>

<style>
.c-carousel {
  position: relative;
  display: flex;
  width: 100%;
  height: auto;
  min-height: 4em;
  flex-direction: column;
  overflow-x: hidden;

  .slides {
    display: flex;
    height: 100%;
    min-height: 100%;
    flex-direction: row;

    .slide {
      position: relative;
      display: flex;
      width: 100%;
      min-width: 100%;
      height: auto;
      object-fit: cover;
    }
  }

  .dots {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    height: 2.125em;
    justify-content: center;
    gap: 0.75em;

    .dot {
      position: relative;
      z-index: 1;
      display: flex;
      width: 0.625em;
      height: 0.625em;
      border-radius: 100%;
      margin: 0.75em 0;
      background-color: white;
      cursor: pointer;
      opacity: 0.8;
      transition: all 0.3s ease;

      &.active,
      &:hover {
        box-shadow: var(--box-shadow-1);
        opacity: 1;
      }
    }
  }

  .button {
    &.button-left,
    &.button-right {
      position: absolute;
      top: calc(50% - 1.5em);
      display: flex;
      padding: 0.925em;
      border-radius: 100%;
      background-color: white;
      box-shadow: var(--box-shadow-1);
      cursor: pointer;

      &.disabled {
        box-shadow: none;
        cursor: default;
        opacity: 0.8;
      }

      .c-icon {
        height: 1.375em;
        color: var(--color-secondary);
        opacity: 0.5;
      }
    }

    &.button-left {
      left: 1em;

      .c-icon {
        transform: rotate(0.5turn);
      }
    }

    &.button-right {
      right: 1em;
    }
  }
}
</style>
